import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { getLabAppointments } from '../../apiCalls/appointments';
import moment from "moment/moment";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "booked", label: "Booked" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "cancelledbyuser", label: "Cancelled by User" },
  { value: "paid", label: "Paid" },
];

const statusMeta = {
  booked: { label: "Booked", cls: "status-booked" },
  paid: { label: "Paid", cls: "status-paid" },
  completed: { label: "Completed", cls: "status-completed" },
  cancelled: { label: "Cancelled", cls: "status-cancelled" },
  cancelledbyuser: { label: "Cancelled by User", cls: "status-cancelledbyuser" },
};

const getStatusMeta = (s = "") => statusMeta[s.toLowerCase().replace(/\s+/g, "")] || { label: s, cls: "status-booked" };

const AppointmentList = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user);
  const { _id } = users?.currentUser;
  const { appointments } = useSelector((state) => state.appointments);

  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(0);
  const [paginationStart, setPaginationStart] = useState(1);

  const page = parseInt(searchParams.get("page")) || 1;
  const itemsPerPage = 30;
  const visiblePages = 3;

  // ── Filters & sort state ──────────────────────
  const [statusFilter, setStatusFilter] = useState("");
  const [dateSort, setDateSort] = useState("desc"); // "asc" | "desc"
  const [search, setSearch] = useState("");

  // Fetch when page changes
  useEffect(() => {
    getLabAppointments(dispatch, _id, page, itemsPerPage, { status: statusFilter });
  }, [dispatch, page, statusFilter]);

  // ── Client-side filter + sort ──────────────────
  const displayed = useMemo(() => {
    let list = appointments ? [...appointments] : [];

    // search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.appointerName?.toLowerCase().includes(q) ||
          a.phoneNumber?.includes(q) ||
          a.testName?.toLowerCase().includes(q)
      );
    }

    // date sort
    list.sort((a, b) => {
      const ta = new Date(a.appointmentTime).getTime();
      const tb = new Date(b.appointmentTime).getTime();
      return dateSort === "asc" ? ta - tb : tb - ta;
    });

    return list;
  }, [appointments, search, dateSort]);

  // ── Helpers ──────────────────────────────────
  const handlePageChange = (p) => setSearchParams({ page: p });

  const handlePaginationSlide = (dir) => {
    if (dir === "next")
      setPaginationStart((p) => Math.min(p + visiblePages, totalPages - visiblePages + 1));
    else
      setPaginationStart((p) => Math.max(p - visiblePages, 1));
  };

  const resetFilters = () => {
    setStatusFilter("");
    setDateSort("desc");
    setSearch("");
  };

  const hasFilters = statusFilter || dateSort !== "desc" || search;

  return (
    <div className="al-page">

      {/* ── Header ─────────────────────────────── */}
      <div className="al-header">
        <div>
          <h4 className="al-title">Appointments</h4>
          <p className="al-subtitle">
            {displayed.length} record{displayed.length !== 1 ? "s" : ""} found
          </p>
        </div>
      </div>

      {/* ── Filters bar ────────────────────────── */}
      <div className="al-filters">
        {/* Search */}
        <div className="al-search-wrap">
          <i className="bi bi-search al-search-icon" />
          <input
            type="text"
            className="al-search"
            placeholder="Search by name, contact or test…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="al-search-clear" onClick={() => setSearch("")}>
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        {/* Status select */}
        <div className="al-filter-group">
          <label className="al-filter-label">
            <i className="bi bi-funnel" />
            Status
          </label>
          <select
            className="al-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); handlePageChange(1); }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Date sort */}
        <div className="al-filter-group">
          <label className="al-filter-label">
            <i className="bi bi-calendar3" />
            Date
          </label>
          <div className="al-sort-btns">
            <button
              className={`al-sort-btn ${dateSort === "desc" ? "al-sort-btn--active" : ""}`}
              onClick={() => setDateSort("desc")}
              title="Newest first"
            >
              <i className="bi bi-sort-down" /> Newest
            </button>
            <button
              className={`al-sort-btn ${dateSort === "asc" ? "al-sort-btn--active" : ""}`}
              onClick={() => setDateSort("asc")}
              title="Oldest first"
            >
              <i className="bi bi-sort-up" /> Oldest
            </button>
          </div>
        </div>

        {/* Reset */}
        {hasFilters && (
          <button className="al-reset-btn" onClick={resetFilters}>
            <i className="bi bi-arrow-counterclockwise" /> Reset
          </button>
        )}
      </div>

      {/* ── Table ──────────────────────────────── */}
      <div className="al-table-wrap">
        {displayed.length === 0 ? (
          <div className="al-empty">
            <i className="bi bi-calendar2-x al-empty-icon" />
            <p>No appointments found</p>
            {hasFilters && (
              <button className="al-reset-btn" onClick={resetFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="al-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Patient</th>
                <th>Contact</th>
                <th>Test</th>
                <th>Duration</th>
                <th>
                  <span
                    className="al-th-sort"
                    onClick={() => setDateSort((d) => d === "asc" ? "desc" : "asc")}
                    title="Toggle sort"
                  >
                    Appointment Date
                    <i className={`bi bi-arrow-${dateSort === "asc" ? "up" : "down"} al-sort-icon`} />
                  </span>
                </th>
                <th>Booked On</th>
                <th>Charges</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((data, index) => {
                const meta = getStatusMeta(data?.status);
                return (
                  <tr key={data._id || index}>
                    <td className="al-td-num">{index + 1}</td>

                    <td>
                      <div className="al-patient">
                        <div className="al-patient-avatar">
                          {(data?.appointerName || "?")[0].toUpperCase()}
                        </div>
                        <span className="al-patient-name">{data?.appointerName}</span>
                      </div>
                    </td>

                    <td className="al-td-muted">{data?.phoneNumber}</td>

                    <td>
                      <span className="al-test-name">{data?.testName}</span>
                    </td>

                    <td className="al-td-muted">
                      <span className="al-duration">
                        <i className="bi bi-clock" /> {data?.duration} min
                      </span>
                    </td>

                    <td>
                      <div className="al-date-cell">
                        <span className="al-date-primary">
                          {moment(data.appointmentTime).format("DD MMM YYYY")}
                        </span>
                        <span className="al-date-secondary">
                          {moment(data.appointmentTime).format("hh:mm A")}
                        </span>
                      </div>
                    </td>

                    <td className="al-td-muted">
                      {moment(data.createdAt).format("DD MMM YYYY")}
                    </td>

                    <td>
                      <span className="al-charges">₹{data?.charges}</span>
                    </td>

                    <td>
                      <span className={`al-status ${meta.cls}`}>{meta.label}</span>
                    </td>

                    <td>
                      <Link
                        to={`/appointments/appointment-details/${data._id}`}
                        className="al-action-btn"
                        title="View details"
                      >
                        <i className="bi bi-eye" />
                        <span>View</span>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ─────────────────────────── */}
      {totalPages > 1 && (
        <div className="al-pagination">
          {paginationStart > 1 && (
            <button className="al-page-btn" onClick={() => handlePaginationSlide("prev")}>
              <i className="bi bi-chevron-left" />
            </button>
          )}
          {paginationStart > 1 && (
            <>
              <button className="al-page-btn" onClick={() => handlePageChange(1)}>1</button>
              {paginationStart > 2 && <span className="al-page-ellipsis">…</span>}
            </>
          )}
          {Array.from({ length: visiblePages }, (_, i) => {
            const p = paginationStart + i;
            if (p > totalPages) return null;
            return (
              <button
                key={p}
                className={`al-page-btn ${page === p ? "al-page-btn--active" : ""}`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            );
          })}
          {paginationStart + visiblePages - 1 < totalPages && (
            <>
              {paginationStart + visiblePages < totalPages && (
                <span className="al-page-ellipsis">…</span>
              )}
              <button className="al-page-btn" onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </button>
            </>
          )}
          {paginationStart + visiblePages - 1 < totalPages && (
            <button className="al-page-btn" onClick={() => handlePaginationSlide("next")}>
              <i className="bi bi-chevron-right" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
